export type FlowKind = 'login' | 'registration' | 'recovery' | 'verification' | 'settings'
export type FlowSubmission = Record<string, string> | ((signal: AbortSignal) => Promise<Record<string, string>>)

export interface KratosMessage {
  id: number
  text: string
  type: string
  context?: Record<string, unknown>
}

export interface KratosNode {
  type: string
  group: string
  attributes: {
    name?: string
    type?: string
    value?: string | number | boolean
    disabled?: boolean
    readonly?: boolean
    node_type: string
    required?: boolean
    autocomplete?: string
    href?: string
    title?: { text: string }
    text?: { text: string }
  }
  messages?: KratosMessage[]
  meta?: { label?: KratosMessage }
}

export interface KratosFlow {
  id: string
  type: string
  expires_at: string
  issued_at: string
  request_url: string
  return_to?: string
  refresh?: boolean
  requested_aal?: string
  state?: string
  ui: { action: string; method: string; nodes: KratosNode[]; messages?: KratosMessage[] }
}

export interface FlowContext {
  loginChallenge?: string
  returnTo?: string
  refresh: boolean
  aal?: string
}

export function record(value: unknown): Record<string, unknown> {
  return value !== null && typeof value === 'object' ? value as Record<string, unknown> : {}
}

export function isKratosFlow(value: unknown): value is KratosFlow {
  const candidate = record(value)
  const ui = record(candidate.ui)
  return typeof candidate.id === 'string' && typeof ui.action === 'string' && Array.isArray(ui.nodes)
}

// This is a display hint, never identity proof. IDG independently verifies the
// signed email claim against its own saved transaction when binding accounts.
// The migration hint is only a suggestion: any Auth account may be bound, so
// the field stays editable and never replaces a value Kratos already chose
// (for example the current account in a refresh login).
export function prefillMigrationEmail(flow: KratosFlow, kind: FlowKind, email: string): KratosFlow {
  if (!email || email.length > 254 || !['login', 'registration'].includes(kind)) return flow
  const name = kind === 'login' ? 'identifier' : 'traits.email'
  return { ...flow, ui: { ...flow.ui, nodes: flow.ui.nodes.map(node => {
    if (node.type !== 'input' || node.attributes.name !== name || node.attributes.type === 'submit' || node.attributes.value) return node
    return { ...node, attributes: { ...node.attributes, value: email } }
  }) } }
}

export function flowPath(kind: FlowKind): string {
  return kind === 'registration' ? '/register' : `/${kind}`
}

export function flowContext(query: string, flow?: KratosFlow | null, kind?: FlowKind, origin?: string): FlowContext {
  const params = new URLSearchParams(query)
  let returnTo = flow?.return_to || params.get('return_to') || undefined
  let loginChallenge = params.get('login_challenge') || undefined
  // A Kratos browser redirect contains only ?flow=. Recover the SSO bridge
  // context stored in that flow instead of depending on React component state.
  if (returnTo) {
    try {
      const url = new URL(returnTo)
      const internal = origin !== undefined && url.origin === new URL(origin).origin
      if (internal && !loginChallenge && url.pathname === '/hydra/login') loginChallenge = url.searchParams.get('login_challenge') || undefined
      // Recovery deliberately returns to settings before continuing SSO.
      // Unwrap that intermediate URL so restarting recovery cannot nest it.
      if (internal && kind === 'recovery' && url.pathname === '/settings') {
        loginChallenge ||= url.searchParams.get('login_challenge') || undefined
        returnTo = url.searchParams.get('return_to') || undefined
      }
    } catch { /* Relative return URLs cannot carry a bridge challenge. */ }
  }
  return {
    loginChallenge,
    returnTo,
    refresh: flow?.refresh === true || params.get('refresh') === 'true',
    aal: flow?.requested_aal || params.get('aal') || undefined,
  }
}

export function flowUrl(kind: FlowKind, context: FlowContext, id?: string, path = flowPath(kind)): string {
  const params = new URLSearchParams()
  if (id) params.set('flow', id)
  if (context.loginChallenge) params.set('login_challenge', context.loginChallenge)
  if (context.returnTo) params.set('return_to', context.returnTo)
  if (kind === 'login' && context.refresh) params.set('refresh', 'true')
  if (kind === 'login' && context.aal) params.set('aal', context.aal)
  return `${path}${params.size ? `?${params}` : ''}`
}

export function bridgeUrl(challenge: string, origin: string): string {
  const url = new URL('/hydra/login', origin)
  url.searchParams.set('login_challenge', challenge)
  return url.href
}

export function flowReturnTo(context: FlowContext, origin: string, kind?: FlowKind): string | undefined {
  if (kind === 'recovery') return new URL(flowUrl('settings', context), origin).href
  return context.loginChallenge ? bridgeUrl(context.loginChallenge, origin) : context.returnTo
}

export function kratosActionUrl(value: string, origin: string): string {
  const url = new URL(value, origin)
  if (!['http:', 'https:'].includes(url.protocol) || url.username || url.password ||
      !url.pathname.startsWith('/kratos/self-service/')) {
    throw new Error('The authentication service returned an invalid form action.')
  }
  // Keep browser cookies and form submissions on the configured proxy origin.
  return `${origin}${url.pathname}${url.search}`
}

export function browserRedirectUrl(value: string, origin: string): string {
  const url = new URL(value, origin)
  if (!['http:', 'https:'].includes(url.protocol) || url.username || url.password) {
    throw new Error('The authentication service returned an invalid redirect.')
  }
  const current = new URL(origin)
  const authPath = /^\/(?:kratos\/self-service\/|hydra\/|oauth2\/sessions\/logout$|login(?:$|\/)|register(?:$|\/)|recovery(?:$|\/)|verification(?:$|\/)|settings(?:$|\/)|error(?:$|\/))/.test(url.pathname)
  if (authPath && (url.hostname === current.hostname || url.hostname === 'auth.togaphotos.com')) {
    return `${origin}${url.pathname}${url.search}${url.hash}`
  }
  return url.href
}

export function logoutRedirectUrl(value: string, origin: string): string {
  const url = new URL(browserRedirectUrl(value, origin))
  if (url.origin !== origin) throw new Error('The authentication service returned an invalid logout origin.')
  return url.href
}

export function continuationUrl(value: unknown, context: FlowContext, origin: string): string | undefined {
  const body = record(value)
  const details = record(record(body.error).details)
  const entries = Array.isArray(body.continue_with) ? body.continue_with :
    Array.isArray(details.continue_with) ? details.continue_with : []
  const destinations: Record<string, FlowKind> = {
    show_verification_ui: 'verification', show_settings_ui: 'settings', show_recovery_ui: 'recovery',
  }
  for (const entry of entries) {
    const step = record(entry)
    const kind = destinations[String(step.action)]
    const nextFlow = record(step.flow)
    if (kind && typeof nextFlow.id === 'string') {
      return new URL(flowUrl(kind, context, nextFlow.id), origin).href
    }
  }
  const redirect = body.redirect_browser_to || details.redirect_browser_to ||
    entries.map(record).find(step => step.action === 'redirect_browser_to')?.redirect_browser_to
  if (typeof redirect !== 'string') return undefined
  const url = new URL(browserRedirectUrl(redirect, origin))
  // Keep context across full-page transitions to our self-service UI. Never
  // attach it to an external provider or Kratos's built-in Hydra integration.
  if (url.origin === origin && ['/login', '/register', '/settings', '/verification', '/recovery'].includes(url.pathname)) {
    if (context.loginChallenge && !url.searchParams.has('login_challenge')) url.searchParams.set('login_challenge', context.loginChallenge)
    if (context.returnTo && !url.searchParams.has('return_to')) url.searchParams.set('return_to', context.returnTo)
  }
  return url.href
}

export type FlowFailure =
  | { kind: 'flow'; flow: KratosFlow }
  | { kind: 'restart'; id?: string; returnTo?: string }
  | { kind: 'session' }
  | { kind: 'reauthenticate' }
  | { kind: 'error'; message: string }

export function classifyFlowFailure(status: number | undefined, value: unknown): FlowFailure {
  if (isKratosFlow(value)) return { kind: 'flow', flow: value }
  const body = record(value)
  const error = record(body.error)
  const details = record(error.details)
  if (status === 410 || status === 404 || error.id === 'security_csrf_violation') {
    return {
      kind: 'restart',
      id: typeof body.use_flow_id === 'string' ? body.use_flow_id : undefined,
      returnTo: typeof details.return_to === 'string' ? details.return_to : undefined,
    }
  }
  if (error.id === 'session_already_available') return { kind: 'session' }
  if (status === 401 || error.id === 'session_refresh_required' || error.id === 'session_aal2_required') {
    return { kind: 'reauthenticate' }
  }
  return {
    kind: 'error',
    message: typeof error.reason === 'string' ? error.reason : 'Unable to complete this request. Please try again.',
  }
}

export function formNodes(flow: KratosFlow, group: string, kind?: FlowKind): KratosNode[] {
  const names = new Set<string>()
  return flow.ui.nodes.filter(node => {
    // v26 registration separates profile traits from the selected credential.
    const registrationProfile = kind === 'registration' && group === 'password' && node.group === 'profile'
    if (node.group !== 'default' && node.group !== group && !registrationProfile) return false
    if (node.type !== 'input') return node.type === 'text'
    const { name, type } = node.attributes
    if (!name) return false
    // Submit controls with the same name can represent separate actions.
    if (type === 'submit') return true
    if (names.has(name)) return false
    names.add(name)
    return true
  })
}

export function messageType(type: string): 'error' | 'info' | 'success' | 'warning' {
  return type === 'info' || type === 'success' || type === 'warning' ? type : 'error'
}

export type FlowTransition =
  | { kind: 'redirect'; url: string }
  | { kind: 'flow'; flow: KratosFlow; saved: boolean }
  | { kind: 'confirm-session' }
  | { kind: 'reload-flow' }

export function submissionTransition(kind: FlowKind, value: unknown, context: FlowContext, origin: string): FlowTransition {
  const redirect = continuationUrl(value, context, origin)
  if (redirect) return { kind: 'redirect', url: redirect }
  if (isKratosFlow(value)) {
    if (value.state === 'passed_challenge' && kind === 'recovery') {
      return { kind: 'redirect', url: new URL(flowUrl('settings', context), origin).href }
    }
    if (value.state === 'passed_challenge' && kind === 'verification') {
      // The bridge checks the now-verified registration session and resumes
      // the original challenge without another password form.
      if (context.loginChallenge) return { kind: 'redirect', url: bridgeUrl(context.loginChallenge, origin) }
      return { kind: 'redirect', url: new URL(flowUrl('login', { ...context, refresh: false }), origin).href }
    }
    return { kind: 'flow', flow: value, saved: kind === 'settings' && value.state === 'success' }
  }
  if (kind === 'registration') {
    // A new identity is not proof of a browser session; verification and login
    // remain required even if the registration hook supplied no continuation.
    return { kind: 'redirect', url: new URL(flowUrl('login', { ...context, refresh: false }), origin).href }
  }
  return { kind: kind === 'login' ? 'confirm-session' : 'reload-flow' }
}
